import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import Scroll, { scroller } from 'react-scroll';

import { useFullscreen } from 'features/fullscreen';
import { ProductTypeEnum, useProducts } from 'features/products';
import { ContentsItem, useContents } from 'features/contents';
import { Flex, Typography } from 'components';
import { Icon } from 'components/icon';
import { SidebarContentCard } from './components';
import { useParams } from 'react-router-dom';

interface SidebarContentsListProps {
  hasTabs: boolean;
  orientation: 'portrait' | 'landscape';
  windowSize: {
    innerWidth: number;
    innerHeight: number;
    videoWidth: number;
    videoHeight: number;
  };
}

const { Element } = Scroll;

const CustomElement = styled(Element)`
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ElementWrapper = styled('div')`
  ${(props: any) => {
    return `
      width: 100vw;
      height: ${
        props['data-orientation'] === 'landscape' ? `calc(100vh - ${props['data-videoheight'] + (props['data-hastabs'] ? 167 : 122)}px)` : 'auto'
      };

      ${props.theme.mediaQueries.desktop} {
        width: 425px;
        height: ${props['data-hastabs'] ? 'calc(100vh - 81px - 60px)' : 'calc(100vh - 81px)'};
      }
    `;
  }}
`;

const InteractionItem = styled(Element)`
  margin-bottom: ${(props: any) => (props['data-last'] ? 0 : '1px')};
`;

/*
const InteractionItem = styled(Element)`
  background: ${(props: any) => (props['data-active'] ? props.theme.colors.videoTimelineActiveBg : props.theme.colors.videoTimelineBg)};
  position: relative;

  ${Bullet} {
    background: ${(props: any) => (props['data-active'] ? props.theme.colors.videoTimelineActiveBg : props.theme.colors.videoTimelineBg)};

    &::before {
      border: 2px solid ${(props: any) => (props['data-active'] ? props.theme.colors.primary_400 : props.theme.colors.gray_600)};
      background: ${(props: any) =>
        props['data-active']
          ? props.theme.colors.primary_400
          : props['data-resolved']
          ? props.theme.colors.gray_600
          : props.theme.colors.videoTimelineBg};
    }
  }

  &::before {
    content: '';
    display: block;
    width: 2px;
    background: ${({ theme }) => theme.colors.gray_600};
    position: absolute;
    top: 0;
    left: 30px;
  }

  ${(props: any) => {
    return `
      padding: 20px 20px ${props['data-first'] ? '50px' : '20px'} 60px;

      &::before {
        height: ${props['data-first'] ? 'calc(100% - 30px)' : props['data-last'] ? '30px' : '100%'};
        margin-top: ${props['data-first'] ? '30px' : '0'};
      }

      ${props.theme.mediaQueries.desktop} {
        padding: 30px 30px ${props['data-first'] ? '80px' : '30px'} 60px;

        &::before {
          height: ${props['data-first'] ? 'calc(100% - 55px)' : props['data-last'] ? '30px' : '100%'};
          margin-top: ${props['data-first'] ? '55px' : '0'};
        }
      }
    `;
  }}
`;
*/

const ModuleIcon = styled(Icon)`
  width: 26px;
`;

const SidebarModule = ({
  children,
  moduleUuid,
  isActive,
  isModule = true,
  isLast,
}: {
  children: ReactNode;
  moduleUuid: string;
  isActive: boolean;
  isModule: boolean;
  isLast: boolean;
}) => {
  return (
    <InteractionItem key={`interaction-timeline-${moduleUuid}`} data-last={isLast} data-active={isActive} name={`interaction-timeline-${moduleUuid}`}>
      {children}
    </InteractionItem>
  );
};

const SidebarItems = ({
  name,
  items,
  isActive,
  onModuleClick,
  productSlug,
  productClassUuid,
  moduleUuid,
  contentUuid,
  showPercentage = true,
  productType,
}: {
  name: string;
  items: ContentsItem[];
  onModuleClick?: () => void;
  isActive: boolean;
  productSlug: string;
  productClassUuid: string;
  moduleUuid: string;
  contentUuid: string;
  showPercentage?: boolean;
  productType: ProductTypeEnum;
}) => {
  return (
    <Flex flexDirection="column" bg="dark_150">
      <Flex
        alignItems="center"
        flexWrap="nowrap"
        justifyContent="space-between"
        py={3}
        px={3}
        onClick={onModuleClick}
        style={{ cursor: 'pointer' }}
        bg="dark_350"
      >
        <Typography type="type2" color="white" fontWeight="bold" lineHeight={1.3} mr={3}>
          {name}
        </Typography>

        <ModuleIcon icon={!isActive ? 'chevronDown' : 'chevronUp'} />
      </Flex>

      {isActive &&
        items.map((content, contentIndex) => {
          return (
            <SidebarContentCard
              key={`items-item-${contentIndex.toString()}-${content.uuid}`}
              content={content}
              index={contentIndex}
              productSlug={productSlug}
              classUuid={productClassUuid}
              moduleUuid={moduleUuid}
              isCurrent={contentUuid === content.uuid}
              showPercentage={false}
              productType={productType}
            />
          );
        })}
    </Flex>
  );
};

const SidebarContentsList = ({ hasTabs, windowSize, orientation }: SidebarContentsListProps) => {
  const [currentTimelineUuid, setCurrentTimelineUuid] = useState('');

  const { productSlug, productClassUuid, moduleUuid } = useParams();

  const [isFullscreen] = useFullscreen();
  const { productEvolutionDetails } = useProducts();
  const { contentDetails } = useContents();

  const goTo = (timelineUuid: string) => {
    scroller.scrollTo(`interaction-timeline-${timelineUuid}`, {
      containerId: 'contentsListElement',
      spy: true,
      smooth: true,
      duration: 250,
    });
  };

  useEffect(() => {
    if (currentTimelineUuid) goTo(currentTimelineUuid);
  }, [currentTimelineUuid]);

  useEffect(() => {
    if (productEvolutionDetails?.timelines?.length && contentDetails) {
      if (contentDetails) setCurrentTimelineUuid(contentDetails.productClassTimelineUuid);
    }
  }, [productEvolutionDetails, contentDetails]);

  return (
    <ElementWrapper data-fullscreen={isFullscreen} data-hastabs={hasTabs} data-videoheight={windowSize.videoHeight} data-orientation={orientation}>
      <CustomElement
        name="contentsList"
        className="element fixed-size"
        id="contentsListElement"
        style={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
        }}
      >
        {!!productEvolutionDetails?.timelines?.length &&
          productEvolutionDetails?.timelines.map((timeline, timelineIndex) => {
            return (
              <SidebarModule
                moduleUuid={timeline.uuid}
                isModule
                isActive={timeline.uuid === currentTimelineUuid}
                isLast={timelineIndex === productEvolutionDetails?.timelines?.length - 1}
              >
                <SidebarItems
                  name={timeline.name}
                  items={timeline.items}
                  isActive={timeline.uuid === currentTimelineUuid}
                  onModuleClick={() => {
                    setCurrentTimelineUuid(currentTimelineUuid === timeline.uuid ? '' : timeline.uuid);
                  }}
                  productSlug={productSlug}
                  productClassUuid={productClassUuid}
                  moduleUuid={moduleUuid}
                  contentUuid={contentDetails?.contentUuid}
                  productType={contentDetails?.productType}
                />
              </SidebarModule>
            );
          })}

        {!!productEvolutionDetails?.items?.length && (
          <SidebarItems
            name={contentDetails?.productName}
            items={productEvolutionDetails?.items}
            isActive
            productSlug={productSlug}
            productClassUuid={productClassUuid}
            moduleUuid={moduleUuid}
            contentUuid={contentDetails?.contentUuid}
            showPercentage={contentDetails?.productType !== ProductTypeEnum.MasterClass}
            productType={contentDetails?.productType}
          />
        )}
      </CustomElement>
    </ElementWrapper>
  );
};

export default SidebarContentsList;

// import { useProducts } from 'features/products';
// import React from 'react';
// import { SidebarContens, SidebarModules } from './components';
// import { useContents } from 'features/contents';

// interface SidebarContentsListProps {}

// const SidebarContentsList = ({}: SidebarContentsListProps) => {
//   const { contentDetails } = useContents();
//   const { productEvolutionDetails } = useProducts();

//   return (
//     <>
//       {!!productEvolutionDetails?.timelines.length && <SidebarModules timelines={productEvolutionDetails?.timelines} />}
//       {!!productEvolutionDetails?.items.length && <SidebarContens name={contentDetails?.productName} items={productEvolutionDetails?.items} />}
//     </>
//   );
// };

// export default SidebarContentsList;
